import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DeliveryTimerProps {
  startTime: number;
  estimatedMinutes: number;
}

export default function DeliveryTimer({ startTime, estimatedMinutes }: DeliveryTimerProps) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [startTime]);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const estimatedSeconds = estimatedMinutes * 60;
  const remaining = Math.max(0, estimatedSeconds - elapsed);
  const progress = Math.min(100, (elapsed / estimatedSeconds) * 100);
  const isOvertime = elapsed > estimatedSeconds;
  
  return (
    <div className="bg-white rounded-2xl p-4 shadow-xl">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-gray-600 mb-1">{t('timeElapsed') || 'Time Elapsed'}</p>
          <p className="text-2xl font-bold text-gray-900">{formatTime(elapsed)}</p>
        </div>
        
        <div className="text-right">
          <p className="text-xs text-gray-600 mb-1">{t('eta') || 'ETA'}</p>
          <p className={`text-2xl font-bold ${isOvertime ? 'text-red-600' : 'text-[#006491]'}`}>
            {isOvertime ? '+' : ''}{formatTime(Math.abs(remaining))}
          </p>
        </div>
      </div>
      
      <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`absolute top-0 left-0 h-full transition-all duration-1000 ${
            isOvertime ? 'bg-red-500' : 'bg-[#006491]'
          }`}
          style={{ width: `${Math.min(100, progress)}%` }}
        ></div>
      </div>
      
      {isOvertime && (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
          <i className="ri-alarm-warning-line"></i>
          <span>{t('overtimeWarning') || 'Running behind schedule'}</span>
        </div>
      )}
    </div>
  );
}
